import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { isEqual } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { ACTUAL_CLINIC_TYPES } from "constants/clinics";
import { SCHEDULING_V2_FEATURE } from "constants/feature-flags";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser
} from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { CLINICS_QUERY, useLazyGetClinicsQuery } from "services/clinics";
import { Clinic, ClinicsResponse } from "services/types/clinics.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import {
  applyClinicsFilter,
  clinicsFilterReady,
  selectClinicsFilterIsReady
} from "../../scheduler-slice";
import useStorage from "./use-storage.hook";

type ClinicsFilterProviderProps = {
  children?: React.ReactNode;
  disableStorage?: boolean;
};

type Ctx = {
  selected: Clinic[];
  clinics: ClinicsResponse | undefined;
  selectAll: () => void;
  deselectAll: () => void;
  handleChange: (value: Clinic[]) => void;
  showServiceLocations: boolean;
  setShowServiceLocations: (value: boolean) => void;
  currentClinicSelectedStorage: any;
};

const ClinicsFilterContext = createContext<Ctx | null>(null);

export function ClinicsFilterProvider({
  children,
  disableStorage
}: ClinicsFilterProviderProps) {
  const {
    defaultClinics: defaultValues,
    updateStorage,
    contextKey
  } = useStorage(disableStorage);
  const currentClinicSelectedStorage = setupLocalStorageAPI(
    "currentClinicSelected"
  );

  const flags = useFlags();
  const schedulingV2FeatureEnabled = flags[SCHEDULING_V2_FEATURE];

  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentUser = useAppSelector(selectCurrentUser);
  const isReady = useAppSelector(selectClinicsFilterIsReady);

  const { userPermissions } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const [selected, setSelected] = useState<Clinic[]>([]);
  const [showServiceLocations, setShowServiceLocations] = useState(false);

  // Track if user has manually changed selection
  const userTouchedRef = useRef(false);
  const prevContextKey = useRef(contextKey);

  // ----- Stable params & key -----
  const params = useMemo(
    () => ({
      query: CLINICS_QUERY,
      type: showServiceLocations ? undefined : ACTUAL_CLINIC_TYPES,
      region: currentRegion?.id,
      user:
        currentRegion ||
        viewOwnScheduleOnly ||
        currentUser?.show_own_clinics_only
          ? currentUser?.id
          : undefined
    }),
    [
      currentRegion,
      currentUser?.id,
      showServiceLocations,
      viewOwnScheduleOnly,
      currentUser?.show_own_clinics_only
    ]
  );

  const paramsKey = useMemo(() => JSON.stringify(params), [params]);

  const readyToFetch = useMemo(
    () => Boolean(currentClinic && currentUser?.id),
    [currentClinic, currentUser?.id]
  );

  const [getClinics, { data: clinics = [], isFetching, isUninitialized }] =
    useLazyGetClinicsQuery();
  const isLoading = isUninitialized || isFetching;

  // Fire exactly once per effective params key
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!readyToFetch) return;
    if (lastFetchKeyRef.current === paramsKey) return;
    lastFetchKeyRef.current = paramsKey;
    getClinics(params);
  }, [readyToFetch, paramsKey, getClinics, params]);

  useEffect(() => {
    if (contextKey !== prevContextKey.current) {
      // Clinic filter: Clear user-touched flag and selections
      userTouchedRef.current = false;
      setSelected([]);
      initializedRef.current = false;
      lastFetchKeyRef.current = null;
      prevContextKey.current = contextKey;
    }
  }, [contextKey]);

  // ----- Selection helpers -----
  const handleChange = useCallback(
    (value: Clinic[]) => {
      const newValues = value.filter(Boolean);
      userTouchedRef.current = true; // Mark as user-touched
      setSelected(newValues);
      updateStorage(
        "clinics",
        newValues.map(({ id }) => id)
      );
    },
    [updateStorage]
  );

  const validateIds = (values: number[], list: number[]) =>
    values.filter((v) => list.includes(v));

  const selectAll = useCallback(
    () => handleChange(clinics),
    [handleChange, clinics]
  );
  const deselectAll = useCallback(() => handleChange([]), [handleChange]);

  // Compute selected from stored defaults + available clinics
  const selectedClinics = useMemo(() => {
    if (!clinics?.length) return [];

    // If V2 is enabled, use all available clinics
    if (schedulingV2FeatureEnabled) {
      return clinics;
    }

    const validIds = validateIds(
      defaultValues || [],
      clinics.map(({ id }) => id)
    );
    const mapped = validIds
      .map((id) => clinics.find((c) => c.id === id))
      .filter(Boolean) as Clinic[];
    return mapped.length ? mapped : [];
  }, [defaultValues, clinics, schedulingV2FeatureEnabled]);

  // Initialize selection when data loads / changes materially
  const initializedRef = useRef(false);
  useEffect(() => {
    if (isLoading) return;

    const shouldInit =
      !initializedRef.current ||
      (!userTouchedRef.current && !isEqual(selectedClinics, selected)) ||
      !isReady;

    if (shouldInit) {
      // Only reset if user hasn't manually changed selection
      if (!userTouchedRef.current) {
        setSelected(selectedClinics);
        updateStorage(
          "clinics",
          selectedClinics.map(({ id }) => id)
        );
      }
      dispatch(clinicsFilterReady());
      initializedRef.current = true;
    }
  }, [isLoading, selectedClinics, selected, isReady, updateStorage, dispatch]);

  // Apply clinics filter to store only when the effective list changes
  const lastAppliedIdsRef = useRef<number[] | null>(null);
  useEffect(() => {
    if (!isReady) return;

    const currentClinicSelected = disableStorage
      ? selected
      : schedulingV2FeatureEnabled
        ? currentClinicSelectedStorage.getData(false)
          ? [currentClinic]
          : selected
        : selected;

    const ids =
      currentClinicSelected?.filter(Boolean)?.map(({ id }) => id) ?? [];

    if (
      !lastAppliedIdsRef.current ||
      !isEqual(lastAppliedIdsRef.current, ids)
    ) {
      lastAppliedIdsRef.current = ids;
      dispatch(applyClinicsFilter(ids));
    }
  }, [
    isReady,
    schedulingV2FeatureEnabled,
    currentClinicSelectedStorage,
    currentClinic,
    clinics,
    selected,
    disableStorage,
    dispatch
  ]);

  const value = useMemo<Ctx>(() => {
    return {
      selected,
      clinics,
      handleChange,
      selectAll,
      deselectAll,
      showServiceLocations,
      setShowServiceLocations,
      currentClinicSelectedStorage
    };
  }, [
    selected,
    clinics,
    handleChange,
    selectAll,
    deselectAll,
    showServiceLocations,
    currentClinicSelectedStorage
  ]);

  return (
    <ClinicsFilterContext.Provider value={value}>
      {children}
    </ClinicsFilterContext.Provider>
  );
}

export default function useClinics() {
  const ctx = useContext(ClinicsFilterContext);
  if (!ctx)
    throw new Error("useClinics must be used within a ClinicsFilterProvider");
  return ctx;
}
